import * as ExcelJS from 'exceljs';
import FileSaver from 'file-saver';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { ParserHlp } from 'src/lib/fliko/helpers/parser.helper';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { LocLabel } from '../components/clickable-row-table/models/data-table-config.models';
import { LocalizationHelper } from '../helpers/localization.helper';
import { LocalizationService } from '../services/localization.service';

export namespace ExcelFile {
  export interface Column<T = any> {
    header: LocLabel;
    key: keyof T & string;
    width?: number;
    outlineLevel?: number;
    hidden?: boolean;
    style: Partial<ExcelJS.Style>;
    filterButton?: boolean;
  }

  export interface HeaderOpt {
    rowOffset?: number;
    height?: number;
    frozen?: boolean;
  }
}

// TODO: return wrapped values instead of raw ExcelJS objects
export class ExcelFileFactory {
  // experiment: width(1000) -> 125 units -> 24,5cm -> 149 chars of lorem ipsum;
  // height 50 units -> 1,76cm, default one row 0,53cm
  // ! WIDTH UNITS =/= HEIGHT UNITS !!!
  public static readonly CHAR_W = 0.8389261744966443; // 125 / 149
  public static readonly CHAR_H = 15.05681818181818; // 50 * 0,53 / 1,76 ; default row height i guess

  public static readonly DEFAULT_W = 8.673469387755102; // default row - 1,7cm

  // !UNFORTUNATELY it's not always like that so i chose some magic number like Microsoft did
  public static readonly PX_PER_W = 8; // 8.43 characters ~ 64px => 1 character ~ 64/8.43 px

  private static readonly HEADER_ALIGNMENT: Partial<ExcelJS.Alignment> = {
    horizontal: 'center',
    vertical: 'middle',
    wrapText: true
  };

  private workBook: ExcelJS.Workbook;
  private headerRows: { sheet: ExcelJS.Worksheet; rowIndex: number; columnCount: number }[] = [];

  constructor() {
    this.workBook = new ExcelJS.Workbook();
  }

  public getWorkbook(): ExcelJS.Workbook {
    return this.workBook;
  }

  /**
   * Add new worksheet to excel file
   * @param name   name of worksheet
   * @param frozen (optional) - if first row should be frozen
   * @return reference to added worksheet (to put in other functions - one file can have multiple worksheets)
   */
  public addSheet(
    name: string,
    columns: ExcelFile.Column[],
    rows: any[],
    ls: LocalizationService,
    headerOpt?: ExcelFile.HeaderOpt
  ): ExcelJS.Worksheet {
    const worksheet = this.workBook.addWorksheet(
      name,
      headerOpt?.frozen ? { views: [{ state: 'frozen', ySplit: 1 + (headerOpt.rowOffset ?? 0) }] } : undefined
    );

    const lh = new LocalizationHelper(ls);

    worksheet.columns = columns.map((x) => {
      return { ...x, header: StrHlp.capitalize(lh.locLabel(x.header)) };
    });

    worksheet.addRows(rows);

    if (headerOpt?.rowOffset) worksheet.insertRows(0, new Array(headerOpt.rowOffset));

    const headerRowIndex = 1 + (headerOpt?.rowOffset ?? 0);
    if (headerOpt?.height) worksheet.getRow(headerRowIndex).height = headerOpt.height;
    this.headerRows.push({ sheet: worksheet, rowIndex: headerRowIndex, columnCount: columns.length });
    this.setHeaderStyle(worksheet, headerRowIndex, columns.length);

    return worksheet;
  }

  private setHeaderStyle(sheet: ExcelJS.Worksheet, rowIndex: number, columnCount: number): void {
    const headerRow = sheet.getRow(rowIndex);

    for (let colIndex = 1; colIndex <= columnCount; colIndex++) {
      const cell = headerRow.getCell(colIndex);
      cell.style = ObjHlp.merge(cell.style, { alignment: ExcelFileFactory.HEADER_ALIGNMENT });
    }
  }

  private applyHeaderStyles(): void {
    this.headerRows.forEach((header) => this.setHeaderStyle(header.sheet, header.rowIndex, header.columnCount));
  }

  /**
   * Apply Expression or CellIs as conditional formatting to specific columns
   * @param sheet          Excel Worksheet
   * @param rule           rule structure to apply
   * @param startColumn    first column to apply formatting
   * @param endColumn      last column to apply formatting; if undefined it only apples for startColumn
   * @param includeHeaders if formatting should include headers
   */
  public addConditionalFormattingToColumns(
    sheet: ExcelJS.Worksheet,
    rule: {
      type: 'expression' | 'cellIs';
      formulae: string[];
      style: any;
      priority: number;
      operator?: ExcelJS.CellIsOperators;
    },
    startColumn: string,
    endColumn: string | undefined = undefined,
    includeHeaders: boolean = false
  ) {
    const startCell = includeHeaders ? `${startColumn}1` : `${startColumn}2`;
    const lastRowNumber = sheet.actualRowCount;
    const endCell = endColumn ? `${endColumn}${lastRowNumber}` : `${startColumn}${lastRowNumber}`;
    if (rule.type === 'expression') {
      sheet.addConditionalFormatting({
        ref: `${startCell}:${endCell}`,
        rules: [
          {
            type: rule.type,
            formulae: rule.formulae,
            style: rule.style,
            priority: rule.priority
          }
        ]
      });
    } else {
      sheet.addConditionalFormatting({
        ref: `${startCell}:${endCell}`,
        rules: [
          {
            type: rule.type,
            formulae: rule.formulae,
            style: rule.style,
            priority: rule.priority,
            operator: rule.operator
          }
        ]
      });
    }
  }

  /**
   * Save excel workbook to file
   * @param filename file name WITHOUT extension
   */
  public saveToFile(filename: string): Promise<void> {
    this.applyHeaderStyles();
    return this.workBook.xlsx.writeBuffer().then((buffer) => {
      // FIXME works only first time
      FileSaver.saveAs(new Blob([buffer]), ParserHlp.sanitizeFileName(filename, '.xlsx'));
    });
  }

  /**
   * Apply style to excel sheet element
   * @param sheet
   * @param rowIndex    0 -> apply to whole column
   * @param columnIndex 0 -> apply to whole row
   * @param style       fill, border and font settings
   * @param patch       determines, if current style should be patched or overridden by new
   */
  public setStyle(
    sheet: ExcelJS.Worksheet,
    rowIndex: number,
    columnIndex: number | string,
    style: Partial<ExcelJS.Style>,
    patch: boolean = false
  ): void {
    // hack: Column does NOT implement Style, because does not have numFormat xDDD
    const setFields = (x: ExcelJS.Style | ExcelJS.Column) => {
      ObjHlp.keys(style).forEach((key) => (x[key] = style[key] as any));
    };

    const patchFields = (row: ExcelJS.Row, col: ExcelJS.Column, cell: ExcelJS.Cell) => {
      ObjHlp.keys(style).forEach((key) => {
        cell[key] = typeof cell[key] == 'object'
          ? ObjHlp.merge(col[key], row[key], cell[key], style[key])
          : (style[key] as any); // prettier-ignore
      });
    };

    const apply = patch ? patchFields : setFields;
    const opt = { includeEmpty: true };

    if (columnIndex == 0) {
      const row = sheet.getRow(rowIndex);
      row.eachCell(opt, (cell, columnIndex) => apply(row, sheet.getColumn(columnIndex), cell));
    } else if (rowIndex == 0) {
      const column = sheet.getColumn(columnIndex);
      column.eachCell(opt, (cell, rowIndex) => apply(sheet.getRow(rowIndex), column, cell));
    } else {
      apply(sheet.getRow(rowIndex), sheet.getColumn(columnIndex), sheet.getCell(`${columnIndex}${rowIndex}`));
    }
  }

  public expandRow(sheet: ExcelJS.Worksheet, rowIndex: number, ...columnIndexes: (number | string)[]): void {
    const row = sheet.getRow(rowIndex);
    let lines = 1;

    for (const columnIndex of columnIndexes) {
      const width = sheet.getColumn(columnIndex).width ?? ExcelFileFactory.DEFAULT_W;
      const wrappedLines = row
        .getCell(columnIndex)
        .text.split(/\r?\n/)
        .reduce((total, text) => {
          // Width needed to fit this part of the cell's content in a single row
          const dWidth = text.length * ExcelFileFactory.CHAR_W;
          return total + Math.max(1, Math.ceil(dWidth / width));
        }, 0);
      lines = Math.max(lines, wrappedLines);
    }

    row.height = Math.max(row.height ?? ExcelFileFactory.CHAR_H, lines * ExcelFileFactory.CHAR_H);
  }

  /**
   * Return next Excel column index
   * @param index
   * @example
   * nextColumnIndex(F)  -> G
   * nextColumnIndex(Z)  -> AA
   * nextColumnIndex(AF) -> AG
   *
   */
  public static nextColumnIndex(index: string): string {
    index = index.toUpperCase();
    for (let i = index.length - 1; i >= 0; i--) {
      const char = index.charCodeAt(i);
      if (char == 90) {
        // ? if "Z": replace with "A", and move to previous letter (e.g. AZ -> BA)
        index = StrHlp.replaceAt(index, i, 'A');
      } else {
        // ? if not "Z": increment letter and return (e.g. AF -> AG)
        return StrHlp.replaceAt(index, i, char + 1);
      }
    }
    // ? if not returned from loop: add "A" in front (e.g. Z -> AA)
    return 'A' + index;
  }

  public static pxToExcelWidth(pixels: number): number {
    return pixels / ExcelFileFactory.PX_PER_W;
  }
}
